import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Table, Button, Card, Spinner } from 'react-bootstrap';
import { FaChevronLeft, FaChevronRight, FaSearch } from 'react-icons/fa';
import api from '../../services/api';
import Layout from '../../components/Layout';
import TopBar from '../../components/TopBar';
import PageHeader from '../../components/PageHeader';
import moment from 'moment';

const YearlyAttendanceCalendar = () => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [attendanceData, setAttendanceData] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showLeaveDetails, setShowLeaveDetails] = useState(false);

  // Generate array of months for the header
  const months = Array.from({ length: 12 }, (_, i) => ({
    month: i + 1,
    name: moment().month(i).format('MMM'),
    days: new Date(year, i + 1, 0).getDate()
  }));

  // Fetch departments
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await api.get('/departments');
        setDepartments(response.data);
      } catch (error) {
        console.error('Error fetching departments:', error);
      }
    };

    fetchDepartments();
  }, []);

  // Fetch attendance data when year or department changes
  useEffect(() => {
    if (year) {
      fetchAttendanceData();
    }
  }, [year, selectedDepartment]);

  const fetchAttendanceData = async () => {
    setIsLoading(true);
    try {
      const response = await api.get(`/attendance/yearly?year=${year}&department_id=${selectedDepartment}`);
      // The API returns { year: XXXX, report: [...] }
      // We need to use the report array for our attendance data
      setAttendanceData(response.data.report || []);
    } catch (error) {
      console.error('Error fetching attendance data:', error);
      setAttendanceData([]); // Ensure we have an empty array on error
    } finally {
      setIsLoading(false);
    }
  };

  // Filter employees based on search term
  const filteredEmployees = attendanceData.filter(emp => {
    // Use emp.name instead of emp.employee_name to match the backend model
    const name = emp.name || '';
    return name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Check if a given date is the last Saturday of the month
  const isLastSaturday = (date) => {
    const currentMoment = moment(date);
    if (currentMoment.day() !== 6) return false; // Not a Saturday

    // Get the last day of the month
    const lastDay = currentMoment.clone().endOf('month');

    // Find the last Saturday of the month
    while (lastDay.day() !== 6) {
      lastDay.subtract(1, 'days');
    }

    // Check if the current date is the last Saturday
    return currentMoment.isSame(lastDay, 'day');
  };

  // Get attendance status for a specific day
  const getAttendanceStatus = (employee, day, month) => {
    // Format the date as YYYY-MM-DD
    const date = moment(`${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`).format('YYYY-MM-DD');
    const currentMoment = moment(date);
    const dayOfWeek = currentMoment.day();
    const isWeekend = dayOfWeek === 0; // Only Sunday is a weekend

    // Generate leave abbreviation (e.g. Annual Leave -> AL)
    const getLeaveAbbreviation = (name) => {
      if (!name) return 'L';
      // Common mappings
      const map = {
        'Annual Leave': 'AL',
        'Sick Leave': 'SL',
        'Casual Leave': 'CL',
        'Maternity Leave': 'ML',
        'Paternity Leave': 'PL',
        'Unpaid Leave': 'UL',
        'Floating Holiday': 'FH',
        'Compensatory Off': 'CO'
      };
      if (map[name]) return map[name];

      // Fallback: First letter of each word, max 2 chars
      return name
        .split(' ')
        .filter(w => w.length > 0)
        .map(w => w[0])
        .join('')
        .toUpperCase()
        .substring(0, 2);
    };

    // Enable debug for specific dates
    const debugDates = [
      '2025-10-30', '2025-10-31', '2025-11-01',
      '2025-10-01', '2025-11-30' // Add more dates as needed
    ];
    const isDebug = debugDates.includes(date);

    if (isDebug) {
      console.log(`\n--- Debugging date: ${date} ---`);
      console.log('Employee:', employee.employee_name);
      console.log('Current date being checked:', date);
      console.log('All leaves for employee:', employee.leaves);
    }

    // 1. First check if there's a specific attendance record for this date
    const attendance = employee.attendance?.find(a => {
      const attDate = moment(a.date).format('YYYY-MM-DD');
      return attDate === date;
    });

    if (attendance) {
      if (isDebug) {
        console.log('Found attendance record:', attendance);
      }

      if (attendance.status === 'present') return { type: 'P', label: 'P', tooltip: 'Present' };
      if (attendance.status === 'absent') return { type: 'A', label: 'A', tooltip: 'Absent' };

      let leaveTooltip = '';
      let leaveCode = 'L';
      if (employee.leaves && employee.leaves.length > 0) {
        const matchingLeave = employee.leaves.find(l => {
          const leaveStart = moment(l.start_date || l.date).startOf('day');
          const leaveEnd = moment(l.end_date || l.date).endOf('day');
          return currentMoment.isBetween(leaveStart, leaveEnd, 'day', '[]');
        });
        if (matchingLeave && matchingLeave.leave_type) {
          leaveTooltip = ` (${matchingLeave.leave_type})`;
          leaveCode = getLeaveAbbreviation(matchingLeave.leave_type);
        }
      }

      if (attendance.status === 'half_day' || attendance.status === 'H') {
        return { type: 'H', label: 'H', tooltip: `Half Day${leaveTooltip}` };
      }
      return { type: 'L', label: leaveCode, tooltip: `Leave${leaveTooltip}` };
    }

    // 2. Check if this date falls within any leave period
    if (employee.leaves && employee.leaves.length > 0) {
      const matchingLeaves = employee.leaves.filter(l => {
        const leaveStart = moment(l.start_date || l.date).startOf('day');
        const leaveEnd = moment(l.end_date || l.date).endOf('day');
        const isMatch = currentMoment.isBetween(leaveStart, leaveEnd, 'day', '[]');

        if (isDebug && isMatch) {
          console.log('Matching leave found:', {
            leave: l,
            leaveStart: leaveStart.format('YYYY-MM-DD'),
            leaveEnd: leaveEnd.format('YYYY-MM-DD'),
            currentDate: currentMoment.format('YYYY-MM-DD')
          });
        }

        return isMatch;
      });

      if (matchingLeaves.length > 0) {
        // Find the most specific leave (exact date match first)
        const leave = matchingLeaves.find(l =>
          moment(l.start_date).format('YYYY-MM-DD') === date ||
          moment(l.end_date).format('YYYY-MM-DD') === date
        ) || matchingLeaves[0];

        // Determine if it's a half day
        const leaveFor = (leave.leave_for || '').toString().toLowerCase();
        const isHalfDay = leave.is_half_day === true ||
          leave.status === 'half_day' ||
          leaveFor.includes('half') ||
          leaveFor === 'h' ||
          leaveFor.includes('morning') ||
          leaveFor.includes('afternoon');

        if (isDebug) {
          console.log('Leave analysis:', {
            leaveFor,
            is_half_day: leave.is_half_day,
            status: leave.status,
            calculatedIsHalfDay: isHalfDay
          });
          console.log('Matching leave records:', matchingLeaves);
          console.log('Selected leave record:', {
            leave,
            isHalfDay,
            leaveFor: leave.leave_for,
            is_half_day: leave.is_half_day,
            status: leave.status
          });
        }

        const leaveCode = getLeaveAbbreviation(leave.leave_type);

        return isHalfDay
          ? {
            type: 'H',
            label: 'H',
            tooltip: `Half Day Leave${leave.leave_for ? ` (${leave.leave_for})` : ''}`
          }
          : {
            type: 'L',
            label: leaveCode,
            tooltip: `Full Day Leave (${leave.leave_type || 'Leave'})`
          };
      }
    }

    // 3. Check if it's the last Saturday of the month
    if (isLastSaturday(date)) {
      return { type: 'S', label: 'S', tooltip: 'Last Saturday' };
    }

    // 4. Check if it's a weekend
    if (isWeekend) {
      return { type: 'W', label: 'W', tooltip: 'Weekend' };
    }

    // 4. Default: No data
    return { type: '', label: '', tooltip: 'No data' };
  };

  // Generate days for a specific month
  const renderDaysHeader = (month) => {
    const days = [];
    const daysInMonth = new Date(year, month, 0).getDate();

    for (let day = 1; day <= daysInMonth; day++) {
      days.push(
        <th key={`${month}-${day}`} className="text-center" style={{ width: '30px', fontSize: '0.8rem' }}>
          {day}
        </th>
      );
    }

    return days;
  };

  // Render attendance cells for a specific month
  const renderAttendanceCells = (employee, month) => {
    const days = [];
    const daysInMonth = new Date(year, month, 0).getDate();

    // Debug log
    console.log(`Rendering month ${month} for ${employee.employee_name} (${employee.id})`);

    for (let day = 1; day <= daysInMonth; day++) {
      const currentDate = new Date(year, month - 1, day);
      const dayOfWeek = currentDate.getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

      // Get status for the day
      const status = getAttendanceStatus(employee, day, month);

      // Debug log for specific date
      if ((month === 11 && day === 1) || (month === 7 && day >= 20 && day <= 24)) {
        console.log(`Date: ${year}-${month}-${day} (${dayOfWeek}, ${isWeekend ? 'Weekend' : 'Weekday'}):`, status);
      }

      days.push(
        <td
          key={`${employee.id}-${month}-${day}`}
          className={`text-center ${getStatusClass(status)}`}
          style={{
            backgroundColor: getStatusClass(status) ? '' : 'transparent',
            color: isWeekend ? 'red' : 'white',
            fontWeight: 'bold',
            cursor: 'pointer',
            padding: '2px 5px',
            fontSize: '0.8rem'
          }}
          title={getStatusTooltip(status) || (isWeekend ? 'Weekend' : 'No data')}
        >
          {status?.label || (isWeekend ? 'W' : '')}
        </td>
      );
    }

    return days;
  };

  // Get CSS class for status cell
  const getStatusClass = (status) => {
    if (!status) return '';
    switch (status.type) {
      case 'P': return 'bg-success';
      case 'A': return 'bg-danger';
      case 'H': return 'bg-warning';
      case 'L': return 'bg-primary';
      case 'S': return 'text-dark'; // Last Saturday with black text
      default: return '';
    }
  };

  // Get tooltip text for status
  const getStatusTooltip = (status) => {
    if (!status) return '';
    if (status.tooltip) return status.tooltip;

    switch (status.type) {
      case 'P': return 'Present';
      case 'A': return 'Absent';
      case 'H': return 'Half Day';
      case 'L': return 'Leave';
      default: return '';
    }
  };

  // Handle year navigation
  const handlePreviousYear = () => setYear(year - 1);
  const handleNextYear = () => setYear(year + 1);

  return (
    <Layout>
      <TopBar />
      <Container fluid className="mt-3">
        <PageHeader title="Yearly Attendance Calendar" />

        <Card className="mb-4">
          <Card.Body>
            <Row className="mb-3">
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Year</Form.Label>
                  <div className="d-flex align-items-center">
                    <Button variant="outline-secondary" size="sm" onClick={handlePreviousYear}>
                      <FaChevronLeft />
                    </Button>
                    <Form.Control
                      type="number"
                      value={year}
                      onChange={(e) => setYear(parseInt(e.target.value) || new Date().getFullYear())}
                      className="mx-2 text-center"
                    />
                    <Button variant="outline-secondary" size="sm" onClick={handleNextYear}>
                      <FaChevronRight />
                    </Button>
                  </div>
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group>
                  <Form.Label>Department</Form.Label>
                  <Form.Select
                    value={selectedDepartment}
                    onChange={(e) => setSelectedDepartment(e.target.value)}
                  >
                    <option value="all">All Departments</option>
                    {departments.map(dept => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>Search Employee</Form.Label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <FaSearch />
                    </span>
                    <Form.Control
                      type="text"
                      placeholder="Search by employee name..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </Form.Group>
              </Col>

              <Col md={2} className="d-flex align-items-end">
                <Form.Check
                  type="switch"
                  id="show-leave-details"
                  label="Show Leave Details"
                  checked={showLeaveDetails}
                  onChange={() => setShowLeaveDetails(!showLeaveDetails)}
                  className="mb-3"
                />
              </Col>
            </Row>

            <div className="d-flex justify-content-between mb-2">
              <Button variant="outline-primary" size="sm" onClick={fetchAttendanceData} disabled={isLoading}>
                {isLoading ? 'Refreshing...' : 'Refresh Data'}
              </Button>
              <div className="d-flex">
                <div className="me-3 d-flex align-items-center">
                  <span className="legend-box bg-success me-1"></span>
                  <small>Present</small>
                </div>
                <div className="me-3 d-flex align-items-center">
                  <span className="legend-box bg-danger me-1"></span>
                  <small>Absent</small>
                </div>
                <div className="me-3 d-flex align-items-center">
                  <span className="legend-box bg-warning me-1"></span>
                  <small>Half Day</small>
                </div>
                <div className="d-flex align-items-center">
                  <span className="legend-box bg-info me-1"></span>
                  <small>Leave</small>
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>

        {isLoading ? (
          <div className="text-center my-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-2">Loading attendance data...</p>
          </div>
        ) : (
          <div className="table-responsive">
            <Table bordered hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th rowSpan="2" className="align-middle">Employee</th>
                  {months.map((month) => (
                    <th
                      key={`month-${month.month}`}
                      colSpan={month.days}
                      className="text-center"
                      style={{ fontSize: '0.9rem' }}
                    >
                      {month.name} {year}
                    </th>
                  ))}
                </tr>
                <tr>
                  {months.flatMap(month => {
                    const daysInMonth = new Date(year, month.month, 0).getDate();
                    return Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => (
                      <th
                        key={`day-${month.month}-${day}`}
                        className="text-center"
                        style={{
                          fontSize: '0.7rem',
                          padding: '2px',
                          minWidth: '30px',
                          width: '30px'
                        }}
                      >
                        {day}
                      </th>
                    ));
                  })}
                </tr>
              </thead>
              <tbody>
                {filteredEmployees.length > 0 ? (
                  filteredEmployees.map((employee) => (
                    <tr key={employee.id}>
                      <td className="fw-bold" style={{ minWidth: '250px' }}>
                        <div className="d-flex flex-column">
                          <div>
                            <span className="me-2">{employee.employee_name || employee.name}</span>
                            <small className="text-muted" style={{ fontSize: '0.7rem' }}>{employee.employee_id}</small>
                          </div>
                          {showLeaveDetails && employee.leaves && employee.leaves.length > 0 && (
                            <div className="small text-muted">
                              {employee.leaves.map((leave, idx) => (
                                <div key={idx}>
                                  {leave.leave_type}: {moment(leave.start_date).format('MMM D')} - {moment(leave.end_date).format('MMM D')}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                      {months.flatMap(month =>
                        renderAttendanceCells(employee, month.month)
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={32} className="text-center py-4">
                      No attendance records found for the selected criteria.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        )}
      </Container>

      <style jsx global>{`
        .legend-box {
          display: inline-block;
          width: 15px;
          height: 15px;
          border: 1px solid #dee2e6;
        }
        
        .table th, .table td {
          width: 30px;
          height: 30px;
          min-width: 30px;
          padding: 0;
          text-align: center;
          vertical-align: middle;
          border: 1px solid #dee2e6;
          box-sizing: border-box;
        }
        
        .table thead th {
          position: sticky;
          top: 0;
          background-color: #f8f9fa;
          z-index: 10;
        }
        
        .table thead th:first-child {
          position: sticky;
          left: 0;
          z-index: 20;
          background-color: #f8f9fa;
        }
        
        tbody tr td:first-child {
          position: sticky;
          left: 0;
          background-color: white;
          z-index: 5;
        }
        
        .table-hover tbody tr:hover td:first-child {
          background-color: #f8f9fa;
        }
      `}</style>
    </Layout>
  );
};

export default YearlyAttendanceCalendar;
